import {useQuery} from '@tanstack/react-query'
import {captureException} from '@sentry/react'
import {QUERY_KEYS} from '@/queryClient.ts'
import {parseAxiosPromise} from '@/axiosInstance.ts'
import {useTranslation} from 'react-i18next'
import {useAssignmentCode} from '@features/ai-chatbot/hooks/use-assignment-code'
import {httpInitChatSession, HttpInitChatSessionResponse} from '@features/ai-chatbot/http/ai-chatbot.http'
import {useChatStore} from '@features/ai-chatbot/store/store.ts'

/**
 * React Query hook to initialize a chat session.
 *
 * The session is created the first time the chat is opened and then stays
 * stable for the whole visit: the query key is constant, so navigating across
 * the app does not spin up a new session and therefore never resets the
 * conversation. The assignment and language captured at that first init are
 * still sent to the backend in the payload — same trade-off Elevate makes with
 * the quote id — and the code is echoed back in the query data so callers can
 * tell what the session was scoped to. To re-scope the session to another
 * assignment the user starts a new conversation, which invalidates the query.
 *
 * The photographer is still identified by the bearer token alone — the backend
 * resolves the user from it.
 */
export const useInitChatSession = () => {
    const {i18n} = useTranslation()
    const language = i18n.language
    const isOpen = useChatStore(state => state.isOpen)
    const assignmentCode = useAssignmentCode()

    return useQuery({
        queryKey: [QUERY_KEYS.CHATBOT_SESSION],
        queryFn: async () => {
            const response = await parseAxiosPromise({
                axiosPromise: httpInitChatSession({language, assignmentCode}),
                responseShape: HttpInitChatSessionResponse,
                onZodError: captureException
            })
            // The code travels with the session, not with the route: consumers
            // must describe the session the backend actually created, and it
            // knows nothing about assignments visited afterwards.
            return {...response, assignmentCode}
        },
        enabled: isOpen,
        staleTime: Infinity,
        retry: 1
    })
}
